import i18next from 'i18next';

export type RelativeTimeResult = {
  display: string;
  exact: string;
};

const pad2 = (n: number) => n.toString().padStart(2, '0');

export function formatExact(dateInput: Date | string | number, locale?: string): string {
  const d = new Date(dateInput);
  // Always 24h per requirement
  const year = d.getFullYear();
  const month = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  const hours = pad2(d.getHours());
  const minutes = pad2(d.getMinutes());
  const seconds = pad2(d.getSeconds());
  const lang = (locale || i18next.language || 'vi').split('-')[0];
  if (lang === 'vi') {
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  }
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

function isSameCalendarDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isYesterday(a: Date, b: Date): boolean {
  const y = new Date(b);
  y.setDate(b.getDate() - 1);
  return isSameCalendarDay(a, y);
}

export function formatRelativeTime(dateInput: Date | string | number, nowInput: Date | number = Date.now(), locale?: string): RelativeTimeResult {
  const d = new Date(dateInput);
  const now = new Date(nowInput);
  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  const t = (key: string, opts?: Record<string, any>): string => String(i18next.t(key, opts as any));
  const exact = formatExact(d, locale);

  let display: string;
  if (diffMs < 0) {
    return { display: exact, exact };
  }

  if (diffSec < 20) {
    display = t('common.relative.justNow');
  } else if (diffSec < 60) {
    display = t('common.relative.oneMinuteAgo');
  } else if (diffHour < 1) {
    display = t('common.relative.minutesAgo', { count: diffMin });
  } else if (isSameCalendarDay(d, now)) {
    display = t('common.relative.hoursAgoToday', { count: diffHour });
  } else if (isYesterday(d, now)) {
    const hour = pad2(d.getHours());
    const minute = pad2(d.getMinutes());
    display = t('common.relative.clockHourYesterday', { hour, minute });
  } else if (diffDay < 7) {
    display = t('common.relative.daysAgo', { count: diffDay });
  } else if (diffDay < 30) {
    const weeks = Math.floor(diffDay / 7) || 1;
    display = t('common.relative.weeksAgo', { count: weeks });
  } else {
    display = exact; // 1 month or more: show exact date/time
  }

  return { display, exact };
}

/**
 * Vietnamese relative time for activity feed (e.g. "4 tuần trước").
 * Uses day-based thresholds so week/month boundaries never produce "0 tháng trước".
 */
export function formatVietnameseTimeAgo(
  dateInput: Date | string | number,
  nowInput: Date | number = Date.now(),
): string {
  const d = new Date(dateInput);
  if (Number.isNaN(d.getTime())) {
    return '';
  }

  const nowMs = new Date(nowInput).getTime();
  const seconds = Math.floor((nowMs - d.getTime()) / 1000);

  if (seconds < 60) {
    return 'vừa xong';
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} phút trước`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} giờ trước`;
  }

  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days} ngày trước`;
  }

  const weeks = Math.floor(days / 7);
  if (days < 30) {
    return `${Math.max(weeks, 1)} tuần trước`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${Math.max(months, 1)} tháng trước`;
  }

  const years = Math.floor(days / 365);
  return `${Math.max(years, 1)} năm trước`;
}
